/**
 * Predefined Credit Block Workflow Rules
 *
 * These are seeded into the workflow rules storage on first load.
 * They mirror the automation patterns found in the Salesforce staging
 * sandbox (CreditBlock__c, Credit_Bureaus__c, Case, Bureau_API_Request__c).
 *
 * When the system admin activates these rules they will fire automatically
 * via the workflow trigger hook in recordStorageService.
 */

import type { WorkflowRule } from '../types/workflowRules';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX || '__c';

export const creditBlockWorkflowRules: Omit<WorkflowRule, 'id' | 'createdDate' | 'lastModified'>[] = [
  // ──────────────────────────────────────────────
  // 1. New Subscription Onboarding
  //    When a new CreditBlock record is created, auto-create 3 onboarding
  //    cases (Processor, Management, Auditor) matching the Salesforce pattern.
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — New Subscription Onboarding',
    description: 'Auto-creates 3 onboarding cases when a new Credit Block subscription is created (Processor, Management, Auditor).',
    objectApiName: `CreditBlock${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onCreateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [],
    },
    actions: [
      {
        id: 'cb_onboard_case_processor',
        type: 'createRecord',
        order: 1,
        config: {
          objectApiName: `Case${CUSTOM_SUFFIX}`,
          fieldMappings: [
            { fieldApiName: 'Subject__c', valueType: 'static', value: 'New Credit Block Subscription Onboarding - Processor' },
            { fieldApiName: 'Type__c', valueType: 'static', value: 'Onboarding' },
            { fieldApiName: 'Status__c', valueType: 'static', value: 'New' },
            { fieldApiName: 'Priority__c', valueType: 'static', value: 'Medium' },
            { fieldApiName: 'Origin__c', valueType: 'static', value: 'System' },
            { fieldApiName: 'Description__c', valueType: 'static', value: 'Lock all client bureaus and verify portal credentials.' },
          ],
          linkToTriggerRecord: true,
        },
      },
      {
        id: 'cb_onboard_case_management',
        type: 'createRecord',
        order: 2,
        config: {
          objectApiName: `Case${CUSTOM_SUFFIX}`,
          fieldMappings: [
            { fieldApiName: 'Subject__c', valueType: 'static', value: 'New Credit Block Subscription Onboarding - Management' },
            { fieldApiName: 'Type__c', valueType: 'static', value: 'Onboarding' },
            { fieldApiName: 'Status__c', valueType: 'static', value: 'New' },
            { fieldApiName: 'Priority__c', valueType: 'static', value: 'Medium' },
            { fieldApiName: 'Origin__c', valueType: 'static', value: 'System' },
            { fieldApiName: 'Description__c', valueType: 'static', value: 'Management review of new subscription setup and client profile.' },
          ],
          linkToTriggerRecord: true,
        },
      },
      {
        id: 'cb_onboard_case_auditor',
        type: 'createRecord',
        order: 3,
        config: {
          objectApiName: `Case${CUSTOM_SUFFIX}`,
          fieldMappings: [
            { fieldApiName: 'Subject__c', valueType: 'static', value: 'New Credit Block Subscription Onboarding - Auditor' },
            { fieldApiName: 'Type__c', valueType: 'static', value: 'Onboarding' },
            { fieldApiName: 'Status__c', valueType: 'static', value: 'New' },
            { fieldApiName: 'Priority__c', valueType: 'static', value: 'Medium' },
            { fieldApiName: 'Origin__c', valueType: 'static', value: 'System' },
            { fieldApiName: 'Description__c', valueType: 'static', value: 'Audit onboarding process for compliance and completeness.' },
          ],
          linkToTriggerRecord: true,
        },
      },
      {
        id: 'cb_onboard_notification',
        type: 'notification',
        order: 4,
        config: {
          message: 'New Credit Block subscription created — 3 onboarding cases have been generated.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'High' as const,
          actionUrl: '/credit-block',
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 2. Bureau Locked
  //    When a Credit_Bureaus record is updated and Stage changes to "Locked".
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Bureau Locked',
    description: 'Sends notification when a bureau is locked.',
    objectApiName: `Credit_Bureaus${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onUpdateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_bureau_locked',
          fieldApiName: 'Stage__c',
          operator: 'equals',
          value: 'Locked',
        },
      ],
    },
    actions: [
      {
        id: 'cb_locked_notify',
        type: 'notification',
        order: 1,
        config: {
          message: 'Bureau {{Bureau_Name__c}} has been locked for client {{Client_Name__c}}.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'Normal' as const,
          actionUrl: '/credit-block',
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 3. Bureau Unlock Requested
  //    When an Unlock Request record is created, update bureau status
  //    and create a reblock case.
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Unlock Request Filed',
    description: 'When an unlock request is created: creates a reblock case (30 day follow-up) and notifies the account owner.',
    objectApiName: `Unlock_Request${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onCreateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [],
    },
    actions: [
      {
        id: 'cb_unlock_reblock_case',
        type: 'createRecord',
        order: 1,
        config: {
          objectApiName: `Case${CUSTOM_SUFFIX}`,
          fieldMappings: [
            { fieldApiName: 'Subject__c', valueType: 'static', value: 'Client Ready for Reblock' },
            { fieldApiName: 'Type__c', valueType: 'static', value: 'Reblock' },
            { fieldApiName: 'Status__c', valueType: 'static', value: 'New' },
            { fieldApiName: 'Priority__c', valueType: 'static', value: 'Medium' },
            { fieldApiName: 'Origin__c', valueType: 'static', value: 'System' },
            { fieldApiName: 'Description__c', valueType: 'static', value: 'Bureau was unlocked — follow up in 30 days to re-establish block.' },
          ],
          linkToTriggerRecord: true,
        },
      },
      {
        id: 'cb_unlock_task',
        type: 'task',
        order: 2,
        config: {
          subject: 'Follow up: Re-block bureau after unlock window',
          description: 'Client bureau was unlocked. Re-block after the approved window expires.',
          assignedTo: 'current_user',
          dueDate: { type: 'relative' as const, days: 30 },
          priority: 'High' as const,
          status: 'Open',
        },
      },
      {
        id: 'cb_unlock_notify',
        type: 'notification',
        order: 3,
        config: {
          message: 'Unlock request filed — reblock case and 30-day follow-up task created.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'High' as const,
          actionUrl: '/credit-block',
        },
      },
      {
        id: 'cb_unlock_email',
        type: 'emailAlert',
        order: 4,
        config: {
          templateId: 'us_unblock_confirmation',
          recipients: [{ type: 'recordOwner' as const }],
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 4. Reblock Completed
  //    When a reblock case is closed, notify.
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Reblock Completed',
    description: 'Notifies when a reblock case is completed.',
    objectApiName: `Case${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onUpdateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_reblock_type',
          fieldApiName: 'Type__c',
          operator: 'equals',
          value: 'Reblock',
        },
        {
          id: 'cond_reblock_closed',
          fieldApiName: 'Status__c',
          operator: 'equals',
          value: 'Closed',
        },
      ],
    },
    actions: [
      {
        id: 'cb_reblock_notify',
        type: 'notification',
        order: 1,
        config: {
          message: 'Bureau re-block completed successfully.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'Normal' as const,
          actionUrl: '/credit-block',
        },
      },
      {
        id: 'cb_reblock_email',
        type: 'emailAlert',
        order: 2,
        config: {
          templateId: 'us_credit_block_reblock_confirmation',
          recipients: [{ type: 'recordOwner' as const }],
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 5. Case Escalation
  //    High/Critical cases open > 48h get escalated.
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Case Escalation',
    description: 'Escalates high/critical priority cases and notifies management.',
    objectApiName: `Case${CUSTOM_SUFFIX}`,
    isActive: false, // Off by default — enable in admin
    triggerType: 'onUpdateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_escalate_priority',
          fieldApiName: 'Priority__c',
          operator: 'equals',
          value: 'Critical',
        },
        {
          id: 'cond_escalate_status',
          fieldApiName: 'Status__c',
          operator: 'notEquals',
          value: 'Closed',
        },
      ],
    },
    actions: [
      {
        id: 'cb_escalate_field',
        type: 'fieldUpdate',
        order: 1,
        config: {
          fieldApiName: 'Escalated__c',
          newValue: true,
        },
      },
      {
        id: 'cb_escalate_task',
        type: 'task',
        order: 2,
        config: {
          subject: 'Escalated Case Review Required',
          description: 'A critical Credit Block case has been escalated. Review and resolve urgently.',
          assignedTo: 'cb_supervisor',
          dueDate: { type: 'relative' as const, days: 1 },
          priority: 'Urgent' as const,
          status: 'Open',
        },
      },
      {
        id: 'cb_escalate_notify',
        type: 'notification',
        order: 3,
        config: {
          message: 'Case escalated to management — immediate attention required.',
          recipients: [
            { type: 'recordOwner' as const },
            { type: 'role' as const, roleId: 'cb_supervisor' },
          ],
          priority: 'High' as const,
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 6. Bureau Unlocked Notification
  //    When a Credit_Bureaus record Stage changes to "Unlocked".
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Bureau Unlocked',
    description: 'Sends notification when a bureau is unlocked.',
    objectApiName: `Credit_Bureaus${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onUpdateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_bureau_unlocked',
          fieldApiName: 'Stage__c',
          operator: 'equals',
          value: 'Unlocked',
        },
      ],
    },
    actions: [
      {
        id: 'cb_unlocked_notify',
        type: 'notification',
        order: 1,
        config: {
          message: 'Bureau {{Bureau_Name__c}} has been unlocked for client {{Client_Name__c}}.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'Normal' as const,
          actionUrl: '/credit-block',
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 7. Case Acknowledgment Email
  //    Auto-send acknowledgment when a Credit Block case is created.
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Case Acknowledgment Email',
    description: 'Sends an acknowledgment email when a new Credit Block case is created.',
    objectApiName: `Case${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onCreateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_ack_type',
          fieldApiName: 'Type__c',
          operator: 'equals',
          value: 'Credit Block',
        },
      ],
    },
    actions: [
      {
        id: 'cb_ack_notify',
        type: 'notification',
        order: 1,
        config: {
          message: 'Credit Block case created: {{Subject__c}}. Acknowledgment email queued.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'Normal' as const,
          actionUrl: '/credit-block',
        },
      },
      {
        id: 'cb_ack_email',
        type: 'emailAlert',
        order: 2,
        config: {
          templateId: 'us_credit_block_case_acknowledgment',
          recipients: [{ type: 'recordOwner' as const }],
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 8. Case Closed Confirmation
  //    Sends confirmation email when any Credit Block case is closed.
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Case Closed Confirmation',
    description: 'Sends a confirmation email and notification when a Credit Block case is closed.',
    objectApiName: `Case${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onUpdateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_closed_type',
          fieldApiName: 'Type__c',
          operator: 'equals',
          value: 'Credit Block',
        },
        {
          id: 'cond_closed_status',
          fieldApiName: 'Status__c',
          operator: 'equals',
          value: 'Closed',
        },
      ],
    },
    actions: [
      {
        id: 'cb_closed_notify',
        type: 'notification',
        order: 1,
        config: {
          message: 'Credit Block case {{Subject__c}} has been closed.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'Normal' as const,
          actionUrl: '/credit-block',
        },
      },
      {
        id: 'cb_closed_email',
        type: 'emailAlert',
        order: 2,
        config: {
          templateId: 'us_credit_block_case_closed_confirmation',
          recipients: [{ type: 'recordOwner' as const }],
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },

  // ──────────────────────────────────────────────
  // 9. Case Reassignment Notification
  //    Notifies when a case owner changes (e.g., from unassigned queue).
  // ──────────────────────────────────────────────
  {
    name: 'Credit Block — Case Reassignment',
    description: 'Notifies the new owner when a Credit Block case is reassigned.',
    objectApiName: `Case${CUSTOM_SUFFIX}`,
    isActive: true,
    triggerType: 'onUpdateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_reassign_type',
          fieldApiName: 'Type__c',
          operator: 'equals',
          value: 'Credit Block',
        },
      ],
    },
    actions: [
      {
        id: 'cb_reassign_notify',
        type: 'notification',
        order: 1,
        config: {
          message: 'Case {{Subject__c}} has been assigned to you.',
          recipients: [{ type: 'recordOwner' as const }],
          priority: 'Normal' as const,
          actionUrl: '/credit-block',
        },
      },
    ],
    createdBy: 'system',
    lastModifiedBy: 'system',
    executionCount: 0,
  },
];

/**
 * Seed credit block workflow rules into localStorage if they don't exist yet.
 * Called once on app init.
 */
export function seedCreditBlockWorkflows(): void {
  const STORAGE_KEY = 'crm_workflow_rules';
  const SEEDED_KEY = 'crm_cb_workflows_seeded_v2';

  // Only seed once
  if (localStorage.getItem(SEEDED_KEY)) return;

  try {
    const existing: WorkflowRule[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

    const newRules: WorkflowRule[] = creditBlockWorkflowRules.map((rule, idx) => ({
      ...rule,
      id: `cb_wfr_${idx + 1}_${Date.now()}`,
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    }));

    const merged = [...existing, ...newRules];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    localStorage.setItem(SEEDED_KEY, 'true');

    console.log(`✅ Seeded ${newRules.length} Credit Block workflow rules`);
  } catch (err) {
    console.error('Failed to seed Credit Block workflows:', err);
  }
}
